<script setup lang="ts">
import { onMounted, computed, watch, ref } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { isNullish } from "@tinyurldev/tu-ui-vue";
import { useRoute, onBeforeRouteUpdate } from "vue-router/composables";

import TeamsLayout from "./Views/TeamsLayout.vue";
import { XTeamsStore } from "./Store/XTeamsStore.ts";
import type { Organization } from "./Data/Models/OrganizationModel.ts";
import UrlPanel from "./Views/UrlPanel.vue";
import TeamUrlViewModel, { type FetchOptions } from "./ViewModels/TeamUrlViewModel.ts";
import AnalyticsPanel from "./Views/AnalyticsPanel.vue";
import AnalyticsDashboard from "./Views/AnalyticsDashboard.vue";
import AnalyticsViewModel from "./ViewModels/AnalyticsViewModel.ts";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";
import { buildFilterQuery } from "@Modules/urls/Helpers/buildFilterQuery";
import XOverlay from "@Modules/shared/store/XOverlay";
import TimePeriods, { defaultPeriod } from "@Modules/analytics/Helpers/TimePeriods";
import { logError } from "@Resources/js/helpers/logger.js";

//
// Setup
const route = useRoute();
const teams = XTeamsStore();
const overlay = XOverlay();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Data 
const isDashboardVisible = ref(false);

//
// Computed

/** if true then we show fake data instead of loading stuff from the server */
const isDebugMode = computed(() => !isNullish(route?.query?.debug));
const debugDataType = computed(() => (route?.query?.dataType as string) || null);

/**
 * If true, charts render the extra controls end-to-end tests need to drive them.
 * Kept separate from debug mode, which also swaps the server data out for fakes.
 */
const isE2eMode = computed(() => !isNullish(route?.query?.e2e));

/** The slug of the current organization and team */
const orgSlug = ref<string>(route?.params?.org || "");
const teamSlug = ref<string | null>(route.params?.team || null);

/** If an alias is provided then we need to load analytics for the alias instead of the team/org */
const aliasSlug = ref<string | null>((route.query?.alias as string) || null);

/** If a tag is provided then we need to load analytics for URLs with that tag */
const tagSlug = ref<string | null>((route.query?.tag as string) || null);

/** The currently loaded organization */
const currentOrganization = computed<Organization | null>(() => teams.getOrganizationBySlug(orgSlug.value));

/** The currently chosen workspace. Personal team is 'personal'. Null if no workspace chosen */
const currentWorkspace = computed(() => {
  if (isNullish(teamSlug.value)) {
    return null;
  }

  return teams.getWorkspaceBySlugAndTeam(orgSlug.value, teamSlug.value) ?? null;
});

/** Returns true if the list of urls is currently being rendered */
const isUrlListVisible = computed(() => {
  if (isMobileView.value) {
    return !isDashboardVisible.value
  }

  return true;
});

/** Returns true if either the URLs or analytics are currently loading */
const isDataLoading = computed(() => teamUrlVM.loading.value || analyticsVM.loading.value);

/** Extracts URL filter parameters from a route query object */
function getUrlFilterParams(query: Record<string, unknown>): Required<Pick<FetchOptions, "filter" | "searchType" | "search">> {
  const tagValue = query?.tag as string | undefined;

  return {
    filter: (query?.list as string) === "archived" ? "archived" : "available",
    searchType: tagValue ? "tag" : ((query?.searchType as string) === "tag" ? "tag" : "alias"),
    search: tagValue || (query?.search as string) || "",
  };
}

/** Current URL filter parameters from the route query string */
const urlFilterParams = computed(() => getUrlFilterParams(route.query));

/** Whether we are currently viewing archived URLs */
const isArchivedFilter = computed(() => urlFilterParams.value.filter === "archived");

/** The currently selected URL alias based on aliasSlug query parameter */
const selectedUrlAlias = computed<UrlAlias | null>(() => {
  if (!aliasSlug.value) {
    return null;
  }
  return teamUrlVM.getUrlAlias(aliasSlug.value);
});

//
// ViewModels

/** Used to fetch and show urls for a team or org */
const teamUrlVM = TeamUrlViewModel({ org: currentOrganization, team: currentWorkspace })
const analyticsVM = AnalyticsViewModel({
    org: currentOrganization,
    team: currentWorkspace,
    alias: aliasSlug,
    tag: tagSlug,
    isDebugMode: isDebugMode.value,
    debugDataType: debugDataType.value,
});

//
// Methods

const initialize = async function (options: Pick<FetchOptions, "filter" | "searchType" | "search"> & {
  updateUrls?: boolean;
  updateAnalytics?: boolean;
} = {}) {
  const {
    updateUrls = true,
    updateAnalytics = true,
    filter = "available",
    searchType = "alias",
    search = "",
  } = options;

  // Some urls disable tracking meaning that we show a placeholder "tracking has been disabled for this link" screen instead of the analytics dashboard.
  // When we refresh the page with a specific url clicked/selected (meaning we are viewing analytics for that link instaead of an aggregate
  // analytics for all team links) we need to make sure that we don't accidentally load click data for a url marked as 
  // "analytics disabled". Analytics data loads async with the list of urls so the only way to ensure that we do not 
  // do that is to wait for the url list to load if we have an ?alias=xyz in the query string when the page loads
  if (updateUrls) {
    if (aliasSlug.value) {
      await teamUrlVM.fetch({ clear: true, includeAlias: aliasSlug.value, filter, searchType, search });
    } else {
      teamUrlVM.fetch({ clear: true, filter, searchType, search });
    }
  }
  if (updateAnalytics) {
    // We only load analytics if the selected url has tracking enabled or we do not have a selected alias (load aggregate dashboard )
    const statsEnabled = teamUrlVM.getUrlAlias(aliasSlug.value ?? undefined)?.mainAlias()?.stats.enabled ?? true;
    if (statsEnabled) {
      analyticsVM.fetch(TimePeriods[defaultPeriod], filter === "archived");
    }
  }
}

/**
 * Widens the analytics scope from the selected link to everything currently in view.
 * The filter query is carried over so only the link selection is cleared.
 */
function onViewAllAnalytics(): void {
  const query = buildFilterQuery(teamUrlVM.lastFetch.value);

  if (currentWorkspace.value) {
    currentWorkspace.value.visit(query);
  } else {
    currentOrganization.value?.visit(query);
  }

  if (isMobileView.value) {
    isDashboardVisible.value = true;

    return;
  }
}

function onUrlSelected(): void {
  if (!isMobileView.value) {
    if (currentWorkspace.value) {
      currentWorkspace.value.visit();
    } else if (currentOrganization.value) {
      currentOrganization.value.visit();
    }
    return;
  }

  isDashboardVisible.value = true;
}

function onToggleStats(enabled: boolean): void {
  if (isNullish(selectedUrlAlias.value)) {
    return;
  }

  teamUrlVM.setStatsTracking(selectedUrlAlias.value, enabled)
    .then((success) => {
      if (success && enabled) {
        initialize({ updateUrls: false });
      }
    })
    .catch(() => { logError("[onToggleStats]: set stats URL failed.") });
}

function hideDashboard(): void {
  isDashboardVisible.value = false;
}

function onEditUrl(): void {
  teamUrlVM.clearErrors();

  if (selectedUrlAlias.value) {
    overlay.showEditFormPopover(
      selectedUrlAlias.value,
      teamUrlVM.updateUrl,
      teamUrlVM.errors.value,
      teamUrlVM.loading.value,
    );
  }
}

//
// Events/Watchers

/** Loads the data when the page is loaded for the first time */
onMounted(() => {
  initialize(urlFilterParams.value);
});

/** hide the dashboard on mobile pages */
watch(isMobileView, (isMobile) => isDashboardVisible.value = !isMobile, { immediate: true });

/**
  * When the route changes but we stay on the same controller (example you click on a different team)
  * then we need to update the current org & team slug and also check for a new alias (meaning
  * the user has clicked on a url). If any of these values have changed then we reload
  * page data
  */
onBeforeRouteUpdate((to, from, next) => {
  const nextAlias = to.query.alias;
  const orgChanged = to.params.org !== from.params.org;
  const teamChanged = to.params.team !== from.params.team;
  const aliasChanged = to.query.alias !== from.query.alias;
  const tagChanged = to.query.tag !== from.query.tag;
  const listChanged = to.query.list !== from.query.list;
  const searchChanged = to.query.search !== from.query.search;
  const searchTypeChanged = to.query.searchType !== from.query.searchType;
  const filterChanged = listChanged || searchChanged || searchTypeChanged;
  const nextStatsEnabled = Array.isArray(nextAlias)
    ? null
    : teamUrlVM.getUrlAlias(nextAlias)?.mainAlias()?.stats.enabled;

  if(orgChanged) {
    orgSlug.value = to.params.org ?? "";
  }

  if(teamChanged) {
    teamSlug.value = to.params.team ?? null;
  }

  if(aliasChanged) {
    aliasSlug.value = (to.query.alias as string) || null;
  }

  if(tagChanged) {
    tagSlug.value = (to.query.tag as string) || null;
  }

  const filterParams = getUrlFilterParams(to.query);
  const lastFetch = teamUrlVM.lastFetch.value;

  const urlsAlreadyMatchRoute =
    lastFetch.orgID === (to.params.org ?? null)
    && (lastFetch.teamID ?? null) === (to.params.team ?? null)
    && lastFetch.filter?.type === filterParams.filter
    && lastFetch.search?.searchType === filterParams.searchType
    && (lastFetch.search?.searchValue ?? "") === filterParams.search;

  const updateUrls = (orgChanged || teamChanged || filterChanged) && !urlsAlreadyMatchRoute;
  const updateAnalytics = (orgChanged || teamChanged || aliasChanged || tagChanged || listChanged) && (nextStatsEnabled ?? true);

  initialize({ updateUrls, updateAnalytics, ...filterParams });

  next();
});
</script>

<template>
  <TeamsLayout
    v-if="currentOrganization"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
    :is-debug="isDebugMode"
    :loading="isDataLoading"
  >
    <div class="organization-dashboard">
      <div
        v-show="isUrlListVisible"
        class="urls-dashboard organization-dashboard__urls"
      >
        <UrlPanel
          :paginator="teamUrlVM.paginator"
          :can-view-urls="teamUrlVM.canViewUrls.value"
          :fetch="teamUrlVM.fetch"
          :loading="teamUrlVM.loading.value"
          :download-urls="teamUrlVM.downloadUrls"
          :selected="aliasSlug"
          :team-identifier="`${orgSlug}-${teamSlug}`"
          :errors="teamUrlVM.errors.value"
          :filter="teamUrlVM.lastFetch.value?.filter?.type"
          :search-type="teamUrlVM.lastFetch.value?.search?.searchType"
          :search="teamUrlVM.lastFetch.value?.search?.searchValue ?? ''"
          @view-all-analytics="onViewAllAnalytics"
          @url-selected="onUrlSelected"
        />
      </div>
      <AnalyticsPanel
        v-if="isDashboardVisible"
        class="organization-dashboard__analytics"
        :org="currentOrganization"
        :team="currentWorkspace"
      >
        <AnalyticsDashboard
          :org="currentOrganization"
          :error="analyticsVM.error.value"
          :current-period="analyticsVM.currentPeriod.value"
          :selected-alias="selectedUrlAlias"
          :selected-tag="tagSlug"
          :archived="isArchivedFilter"
          :fetch="analyticsVM.fetch"
          :download="analyticsVM.download"
          :delete-url="() => selectedUrlAlias && teamUrlVM.deleteUrl(selectedUrlAlias)"
          :toggle-archive="() => selectedUrlAlias && teamUrlVM.archiveUrl(selectedUrlAlias)"
          :set-stats-enabled="onToggleStats"
          :edit-url="onEditUrl"
          :timeline="analyticsVM.timeline.value"
          :timeline-loading="analyticsVM.timelineLoading.value"
          :quickstats="analyticsVM.quickstats.value"
          :quickstats-loading="analyticsVM.quickstatsLoading.value"
          :populartimes="analyticsVM.populartimes.value"
          :populartimes-loading="analyticsVM.populartimesLoading.value"
          :populardays="analyticsVM.populardays.value"
          :populardays-loading="analyticsVM.populardaysLoading.value"
          :topreferrers="analyticsVM.topreferrers.value"
          :topreferrers-loading="analyticsVM.topreferrersLoading.value"
          :toplanguages="analyticsVM.toplanguages.value"
          :toplanguages-loading="analyticsVM.toplanguagesLoading.value"
          :worldtraffic="analyticsVM.worldtraffic.value"
          :worldtraffic-loading="analyticsVM.worldtrafficLoading.value"
          :fetch-world-traffic-map="analyticsVM.fetchWorldTrafficMap"
          :debug="isE2eMode"
          :browserstats="analyticsVM.browserstats.value"
          :browserstats-loading="analyticsVM.generalstatsLoading.value"
          :osstats="analyticsVM.osstats.value"
          :osstats-loading="analyticsVM.generalstatsLoading.value"
          :devicestats="analyticsVM.devicestats.value"
          :devicestats-loading="analyticsVM.generalstatsLoading.value"
          :generalstats-loading="analyticsVM.generalstatsLoading.value"
          @view-links="hideDashboard"
        />
      </AnalyticsPanel>
    </div>
    <!-- <div class="debug-dashboard"> -->
    <!--   dash: {{ curDashboard }} <br> -->
    <!--   org: *{{ orgSlug }}*&{{ route.params.org }}& <br> -->
    <!--   team: *{{ teamSlug }}*&{{ route.params.team }}& <br> -->
    <!--    ORG: {{ currentOrganization }} <br> -->
    <!--   WORKSPACE: {{ currentWorkspace }} -->
    <!-- </div> -->
  </TeamsLayout>
</template>

<style lang="scss" scoped>
.organization-dashboard {
  $self: &;

  display: flex;
  height: 100%;

  &__urls {
    flex: 1;
  }

  &__analytics {
    position: relative;
    flex: 2;
  }
}
</style>
